import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AccountHeroBit from '../../components/account/AccountHeroBit';
import LuxuryInput from '../../components/ui/LuxuryInput';
import LuxuryLoading from '../../components/ui/LuxuryLoading';
import { FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaLock, FaTimesCircle } from 'react-icons/fa';
import { fadeInUp, pageTransition } from "../../components/animations/animations";
import { AuthService } from "../../api/auth.api";

const ResetPassword = () => {
    const navigate = useNavigate();
    const { token } = useParams();

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [isLoading, setIsLoading] = useState(true); // Start with loading true for token validation
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [showError, setShowError] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [tokenValid, setTokenValid] = useState(null); // null = checking, true = valid, false = invalid
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [fieldErrors, setFieldErrors] = useState([]);

    // Check token validity on component mount
    useEffect(() => {
        const validateToken = async () => {
            try {
                setIsLoading(true);

                if (!token) {
                    setTokenValid(false);
                    setApiError('Invalid reset link. Please request a new password reset.');
                    setShowError(true);
                    return;
                }

                // In a real app, you'd validate the token with the backend
                // For now, we'll just check if token exists
                // You can add an API endpoint like: GET /api/auth/validate-reset-token/:token
                console.log('Validating token:', token);

                // Simulate API validation
                setTimeout(() => {
                    setTokenValid(true);
                }, 1000);

            } catch (err) {
                console.error('Token validation error:', err);
                setTokenValid(false);
                setApiError('This password reset link has expired or is invalid.');
                setShowError(true);
            } finally {
                setIsLoading(false);
            }
        };

        validateToken();
    }, [token]);

    const checkPasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        setPasswordStrength(strength);
        return strength;
    };

    const validateForm = () => {
        const newErrors = {};
        const newFieldErrors = [];

        if (!formData.password) {
            newErrors.password = 'Password is required';
            newFieldErrors.push({ field: 'password', message: 'Please enter a new password' });
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
            newFieldErrors.push({ field: 'password', message: 'Minimum 8 characters required' });
        } else if (passwordStrength < 3) {
            newErrors.password = 'Password is too weak';
            newFieldErrors.push({ field: 'password', message: 'Include uppercase, lowercase, numbers & special characters' });
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
            newFieldErrors.push({ field: 'confirmPassword', message: 'Please re-enter your password' });
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
            newFieldErrors.push({ field: 'confirmPassword', message: 'Passwords must match exactly' });
        }

        setErrors(newErrors);
        setFieldErrors(newFieldErrors);


        return Object.keys(newErrors).length === 0;
    };

    const parseBackendError = (error) => {
        if (error.errors && Array.isArray(error.errors)) {
            const fieldErrors = {};
            const errorMessages = [];

            error.errors.forEach(err => {
                if (err.field) {
                    fieldErrors[err.field] = err.message;
                }
                errorMessages.push(err.message);
            });

            setErrors(fieldErrors);
            setApiError(errorMessages.join('. '));
            setFieldErrors(error.errors);
        } else if (error.message) {
            const errorMap = {
                'Invalid or expired reset token': 'This password reset link has expired or is invalid',
                'Password must be at least 8 characters': 'Password must be at least 8 characters',
                'Passwords do not match': 'Passwords do not match',
                'Network Error': 'Connection failed. Please check your internet',
                'Failed to fetch': 'Unable to connect to server'
            };

            setApiError(errorMap[error.message] || error.message);
            setShowError(true);

            setTimeout(() => {
                setShowError(false);
            }, 5000);
        }


    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        setErrors({});
        setApiError('');
        setShowError(false);
        setFieldErrors([]);

        try {
            const res = await AuthService.resetPassword(token, formData.password);

            // Show success animation
            setSuccessMessage('Password reset successful! Redirecting to login...');
            setShowSuccess(true);

            // Clear form
            setFormData({
                password: '',
                confirmPassword: ''
            });

            // Navigate to login after 3 seconds
            setTimeout(() => {
                navigate('/login', {
                    state: {
                        message: 'Password reset successful! Please login with your new password.',
                        type: 'success'
                    }
                });
            }, 3000);

        } catch (err) {
            console.error('Reset password error:', err);
            parseBackendError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputFocus = (field) => {
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });

            setFieldErrors(prev => prev.filter(error => error.field !== field));
        }
    };

    const getStrengthColor = () => {
        switch(passwordStrength) {
            case 5: return 'bg-sage';
            case 4: return 'bg-champagne';
            case 3: return 'bg-coral';
            default: return 'bg-error';
        }
    };

    const getStrengthText = () => {
        switch(passwordStrength) {
            case 5: return 'Excellent';
            case 4: return 'Strong';
            case 3: return 'Good';
            case 2: return 'Weak';
            default: return 'Very Weak';
        }
    };

    // Show loading while validating token
    if (isLoading) {
        return <LuxuryLoading message="Validating your reset link..." />;
    }

    // Show error if token is invalid
    if (tokenValid === false) {
        return (
            <motion.div
                variants={pageTransition}
                initial="initial"
                animate="animate"
                exit="exit"
                className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 py-12"
            >
                <div className="container-luxury max-w-4xl mx-auto px-4">
                    <AccountHeroBit
                        title="Link Expired"
                        subtitle="This password reset link is no longer valid"
                        background="error"
                    >
                        <motion.p
                            variants={fadeInUp}
                            className="text-charcoal/60 text-base"
                        >
                            Password reset links expire after 15 minutes for security reasons.
                        </motion.p>
                    </AccountHeroBit>

                    <motion.div
                        variants={fadeInUp}
                        className="bg-white rounded-3xl shadow-luxury p-8 md:p-12 text-center"
                    >
                        <div className="w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FaExclamationTriangle className="w-10 h-10 text-error/60" />
                        </div>

                        <h2 className="text-2xl font-medium text-charcoal mb-4">
                            Invalid Reset Link
                        </h2>

                        <p className="text-charcoal/60 mb-8 max-w-md mx-auto">
                            {apiError || 'This password reset link has expired or is invalid. Please request a new reset link.'}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/forgot-password"
                                className="px-6 py-3 bg-gradient-to-r from-champagne via-champagne/90 to-champagne/80 text-charcoal font-medium rounded-full hover:from-champagne/90 hover:via-champagne/80 hover:to-champagne/70 transition-all shadow-lg hover:shadow-xl"
                            >
                                Request New Link
                            </Link>

                            <Link
                                to="/login"
                                className="px-6 py-3 border-2 border-nude text-charcoal font-medium rounded-full hover:border-champagne hover:text-champagne transition-all"
                            >
                                Back to Login
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            variants={pageTransition}
            initial="initial"
            animate="animate"
            exit="exit"
            className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 py-12 relative"
        >
            {/* Floating Notifications */}
            <AnimatePresence>
                {showError && apiError && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md"
                    >
                        <div className="bg-error/10 backdrop-blur-sm border border-error/20 rounded-2xl p-4 shadow-luxury">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-error/20 flex items-center justify-center flex-shrink-0">
                                    <FaExclamationTriangle className="w-5 h-5 text-error" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-medium text-error text-sm mb-1">Reset Failed</h4>
                                    <p className="text-charcoal/80 text-sm">{apiError}</p>
                                </div>
                                <button
                                    onClick={() => setShowError(false)}
                                    className="text-charcoal/50 hover:text-charcoal transition-colors flex-shrink-0"
                                >
                                    <FaTimesCircle className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {showSuccess && successMessage && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md"
                    >
                        <div className="bg-sage/10 backdrop-blur-sm border border-sage/20 rounded-2xl p-4 shadow-luxury">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0">
                                    <FaCheckCircle className="w-5 h-5 text-sage" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-medium text-sage text-sm mb-1">Success!</h4>
                                    <p className="text-charcoal/80 text-sm">{successMessage}</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="container-luxury max-w-4xl mx-auto px-4">
                {/* Hero Section */}
                <AccountHeroBit
                    title="Reset Your Password"
                    subtitle="Create a new secure password for your KROMAIC account"
                    background="sage"
                >
                    <motion.p
                        variants={fadeInUp}
                        className="text-charcoal/60 text-base"
                    >
                        Choose a strong password to protect your account
                    </motion.p>
                </AccountHeroBit>

                <motion.div
                    id="reset-form"
                    variants={fadeInUp}
                    className="bg-white rounded-3xl shadow-luxury p-8 md:p-12 max-w-2xl mx-auto relative overflow-hidden"
                >
                    {/* Security notice */}
                    <div className="mb-8 p-4 bg-sage/5 border border-sage/20 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                                <FaLock className="w-5 h-5 text-sage" />
                            </div>
                            <div>
                                <h4 className="font-medium text-sage text-sm mb-1">Security Notice</h4>
                                <p className="text-charcoal/60 text-xs">
                                    This link will expire after you reset your password or after 15 minutes
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* New Password */}
                        <div className="space-y-4">
                            <div className="relative">
                                <LuxuryInput
                                    type="password"
                                    label="New Password"
                                    placeholder="Create a strong password"
                                    value={formData.password}
                                    onChange={(e) => {
                                        setFormData({ ...formData, password: e.target.value });
                                        checkPasswordStrength(e.target.value);
                                    }}
                                    onFocus={() => handleInputFocus('password')}
                                    error={errors.password}
                                    required
                                    size="large"
                                    icon={FaLock}
                                    iconColor={errors.password ? "error" : "charcoal/30"}
                                />

                                {errors.password && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-xs text-error mt-2 flex items-center gap-1"
                                    >
                                        <FaExclamationTriangle className="w-3 h-3" />
                                        {errors.password}
                                    </motion.p>
                                )}
                            </div>

                            {/* Password Strength Meter */}
                            {formData.password && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="space-y-3"
                                >
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs text-charcoal/50">
                                                Password Strength
                                            </span>
                                            <span className={`text-xs font-medium ${getStrengthColor().replace('bg-', 'text-')}`}>
                                                {getStrengthText()}
                                            </span>
                                        </div>
                                        <div className="flex gap-1 mb-1">
                                            {[1, 2, 3, 4, 5].map((level) => (
                                                <motion.div
                                                    key={level}
                                                    initial={{ scaleX: 0 }}
                                                    animate={{ scaleX: level <= passwordStrength ? 1 : 0 }}
                                                    transition={{ delay: level * 0.1 }}
                                                    className={`h-2 flex-1 rounded-full ${
                                                        level <= passwordStrength
                                                            ? getStrengthColor()
                                                            : 'bg-nude/20'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* Password Requirements */}
                                    <div className="space-y-2">
                                        {[
                                            { text: 'At least 8 characters', valid: formData.password.length >= 8 },
                                            { text: 'One uppercase letter', valid: /[A-Z]/.test(formData.password) },
                                            { text: 'One lowercase letter', valid: /[a-z]/.test(formData.password) },
                                            { text: 'One number', valid: /[0-9]/.test(formData.password) },
                                            { text: 'One special character', valid: /[^A-Za-z0-9]/.test(formData.password) }
                                        ].map((req, idx) => (
                                            <motion.div
                                                key={idx}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: idx * 0.1 }}
                                                className="flex items-center gap-2"
                                            >
                                                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${req.valid ? 'bg-sage/20 text-sage' : 'bg-nude/20 text-charcoal/30'}`}>
                                                    {req.valid ? (
                                                        <FaCheckCircle className="w-2 h-2" />
                                                    ) : (
                                                        <div className="w-1 h-1 rounded-full bg-current" />
                                                    )}
                                                </div>
                                                <span className={`text-xs ${req.valid ? 'text-charcoal/60' : 'text-charcoal/40'}`}>
                                                    {req.text}
                                                </span>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div className="relative">
                            <LuxuryInput
                                type="password"
                                label="Confirm New Password"
                                placeholder="Re-enter your new password"
                                value={formData.confirmPassword}
                                onChange={(e) =>
                                    setFormData({ ...formData, confirmPassword: e.target.value })
                                }
                                onFocus={() => handleInputFocus('confirmPassword')}
                                error={errors.confirmPassword}
                                required
                                size="large"
                                icon={FaLock}
                                iconColor={errors.confirmPassword ? "error" : "charcoal/30"}
                            />

                            {errors.confirmPassword && (
                                <motion.p
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="text-xs text-error mt-2 flex items-center gap-1"
                                >
                                    <FaExclamationTriangle className="w-3 h-3" />
                                    {errors.confirmPassword}
                                </motion.p>
                            )}
                        </div>

                        {/* Field-specific error summary */}
                        <AnimatePresence>
                            {fieldErrors.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="bg-error/5 border border-error/20 rounded-2xl p-4"
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-8 h-8 rounded-full bg-error/10 flex items-center justify-center">
                                            <FaExclamationTriangle className="w-4 h-4 text-error" />
                                        </div>
                                        <h4 className="text-sm font-medium text-error">
                                            Please check the following:
                                        </h4>
                                    </div>
                                    <ul className="space-y-1">
                                        {fieldErrors.map((error, idx) => (
                                            <motion.li
                                                key={idx}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: idx * 0.1 }}
                                                className="flex items-center gap-2 text-xs text-error"
                                            >
                                                <div className="w-1.5 h-1.5 rounded-full bg-error" />
                                                {error.message}
                                            </motion.li>
                                        ))}
                                    </ul>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Submit Button */}
                        <motion.div
                            whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                            whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                        >
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`
                                    w-full px-8 py-4 rounded-full
                                    bg-gradient-to-r from-sage via-sage/90 to-sage/80
                                    text-white font-medium text-lg
                                    hover:from-sage/90 hover:via-sage/80 hover:to-sage/70
                                    transition-all duration-300
                                    shadow-lg hover:shadow-xl
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    relative overflow-hidden group
                                `}
                            >
                                <span className="relative flex items-center justify-center gap-3">
                                    {isSubmitting ? (
                                        <>
                                            <span className="w-5 h-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                                            <span className="relative">
                                                Resetting Password
                                                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-white/20">
                                                    <motion.span
                                                        className="absolute h-full bg-white"
                                                        animate={{
                                                            x: ['0%', '100%'],
                                                        }}
                                                        transition={{
                                                            duration: 1.5,
                                                            repeat: Infinity,
                                                            ease: "linear"
                                                        }}
                                                        style={{ width: '30%' }}
                                                    />
                                                </span>
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Reset Password</span>
                                            <FaArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                        </>
                                    )}
                                </span>

                                {/* Shimmer effect */}
                                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                            </button>
                        </motion.div>

                        <div className="text-center space-y-4">
                            <p className="text-charcoal/50 text-sm">
                                Remember your password?{' '}
                                <Link
                                    to="/login"
                                    className="text-champagne hover:text-champagne/80 transition-colors font-medium"
                                >
                                    Back to Login
                                </Link>
                            </p>

                            <p className="text-xs text-charcoal/40">
                                By resetting your password, you agree to our{' '}
                                <Link to="/terms" className="text-charcoal/60 hover:text-champagne">Terms of Service</Link>
                                {' '}and{' '}
                                <Link to="/privacy" className="text-charcoal/60 hover:text-champagne">Privacy Policy</Link>
                            </p>
                        </div>
                    </form>
                </motion.div>
            </div>

            {/* Add custom animation keyframes */}
            <style jsx>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
                    20%, 40%, 60%, 80% { transform: translateX(4px); }
                }
                
                @keyframes successPulse {
                    0%, 100% { 
                        box-shadow: 0 10px 40px rgba(121, 189, 154, 0);
                        transform: scale(1);
                    }
                    50% { 
                        box-shadow: 0 10px 40px rgba(121, 189, 154, 0.4);
                        transform: scale(1.005);
                    }
                }
            `}</style>
        </motion.div>
    );
};

export default ResetPassword;